import { useMemo } from "react";
import { Spin } from "antd";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@kalodata/kds-design";
import { IconLinePlus } from "@kalodata/kds-design-icons";
import { twMerge } from "tailwind-merge";
import {
  CharacterAssetResponseBindState,
  PathsApiCharacterAssetsGetParametersQueryCategory,
} from "@apis/generated/openapi";
import type { CharacterAssetRecord } from "@apis/http/api";
import { useScrollLoad } from "@hooks/useScrollLoad";
import { useAppDispatch } from "@hooks/redux";
import { fetchCharactersPage, type CharacterListParams } from "@pages/library/hooks/characterListFetcher";
import { updateCharacterType, updateFocusType } from "@pages/library/librarySlice";
import { UICharacterPickCard } from "./UICharacterPickCard";
import type { SelectedCharacterPayload } from "./types";

const PAGE_SIZE = 20;

export interface CharacterPickerPanelProps {
  selectedId: number | null;
  onSelect: (payload: SelectedCharacterPayload) => void;
  /** 默认按模特拉取，预留分类扩展位 */
  category?: PathsApiCharacterAssetsGetParametersQueryCategory;
}

/**
 * 选择角色面板：滚动分页加载角色资产列表（默认 MODEL），点击卡片回传 characterAssetId。
 *
 * 复用 Library 的 fetchCharactersPage + useScrollLoad，与 SelectProductModal 的面板范式对齐。
 */
export const CharacterPickerPanel = ({
  selectedId,
  onSelect,
  category = PathsApiCharacterAssetsGetParametersQueryCategory.MODEL,
}: CharacterPickerPanelProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const params = useMemo((): CharacterListParams => ({ category }), [category]);
  const { items, loading, loadingMore, hasMore, sentinelRef } = useScrollLoad<
    CharacterAssetRecord,
    CharacterListParams
  >({
    params,
    pageSize: PAGE_SIZE,
    fetcher: fetchCharactersPage,
  });

  const handleEmptyJump = () => {
    dispatch(updateFocusType("character"));
    dispatch(updateCharacterType("model"));
    navigate("/library");
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Spin />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-[16px] text-kds-fg">
        <div className="size-[110px] rounded-[12px] bg-kds-bg-element flex items-center justify-center text-[40px] text-kds-fg-soft">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="18" r="8" fill="currentColor" opacity="0.45" />
            <path d="M10 40c0-7.732 6.268-14 14-14s14 6.268 14 14" fill="currentColor" opacity="0.3" />
          </svg>
        </div>
        <div className="text-[16px] font-medium leading-[22px] text-kds-fg text-center">
          <Trans>No Characters Yet</Trans>
        </div>
        <Button
          type="reverse"
          size="lg"
          iconPlacement="start"
          icon={<IconLinePlus size={20} className="text-kds-light-reverse" />}
          className={twMerge(
            "bg-kds-dark-reverse text-kds-light-reverse shadow-[0_1px_3px_0_var(--kds-shadow)]",
            "hover:bg-(--palette-slate-11) hover:text-kds-light-reverse",
          )}
          onClick={handleEmptyJump}
        >
          <Trans>Create New Character</Trans>
        </Button>
      </div>
    );
  }

  return (
    <div className="kds-scroll-thin max-h-[60vh] min-h-[400px] overflow-y-auto px-[16px] py-[12px]">
      <div className="grid grid-cols-4 gap-[16px]">
        {items
          .filter((record) => record.bindState !== CharacterAssetResponseBindState.FAILED)
          .map((record) => (
            <UICharacterPickCard
              key={record.id}
              record={record}
              selected={selectedId === record.id}
              onSelect={(r) => onSelect({ characterAssetId: r.id, record: r })}
            />
          ))}
      </div>
      {hasMore && <div ref={sentinelRef} className="h-px" />}
      {loadingMore && (
        <div className="py-[16px] text-center text-[14px] text-kds-fg-subtle">
          <Trans>Loading...</Trans>
        </div>
      )}
    </div>
  );
};
